<template>
  <component
    :is="dynamic"
    :size="size"
  />
</template>

<script lang="ts">
import _ from 'lodash';
import axios from 'axios';
import Vue, { Component, ComponentOptions } from 'vue';
import { defineComponent } from 'vue-demi';

import { BaseCommonContent, BaseItem } from '@/modules/agent-picker';
import { BaseVirtualScrollV2 } from '@/modules/virtual-scroll';
import { SIZE_PROP } from '../../constants';

/* eslint-disable vue/one-component-per-file */
export default defineComponent({
  name: 'AppDialerCustomList',
  props: {
    /** Context can have useful data like session ID */
    context: {
      type: Object,
      default: () => ({}),
    },
    code: {
      type: String,
      default: '',
    },
    size: SIZE_PROP,
  },
  computed: {
    userOptions(): (...args: any[]) => ComponentOptions<Vue> {
      const ARGS = ['_', 'axios', 'context'];
      return this.code.length
        ? (new Function(...ARGS, this.code) as (...args: any[]) => object)
        : () => ({});
    },
    /**
     * Create a component using static template and user-provided script block.
     * Default template values are also provided to avoid warnings.
     */
    dynamic(): Component {
      const components = {
        BaseCommonContent,
        BaseItem,
        BaseVirtualScrollV2,
      };

      const userOptions = this.userOptions(_, axios, this.context);
      const options = this.getDefaultOptions(userOptions.data as () => object);
      const content = _.merge({}, options, _.omit(userOptions, ['data']));

      const { render } = Vue.compile(this.getTemplate());
      return defineComponent({ components, ...(content as any), render });
    },
  },
  methods: {
    getDefaultOptions(data?: () => object): ComponentOptions<Vue> {
      return {
        props: {
          size: SIZE_PROP,
        },
        data() {
          return {
            search: '',
            items: [],
            searchResults: [],
            useSearch: true,
            isLoading: false,
            isDisabled: false,
            /* Custom data has to be merged with default options */
            ...(data ? data() : {}),
          };
        },
        computed: {
          placeholder: () => '',
          dataKey: () => 'id',
          actionsType(this: any) {
            return this.size === 'mobile' ? 'popup' : 'inline';
          },
        },
        methods: {
          getItemKey(item: { id?: string; }) {
            return item.id;
          },
          getItemIcon: (): string => 'person',
          getItemTitle: (): string => '',
          getItemDescription: (): string => '',
          getItemStatus: (): string => '',
          getItemStatusType: (): string => '',
          getItemActions: (): [] => [],
          onSearchChange(this: any, search: string) {
            this.search = search;
          },
          onActionClick(key: string, item: object) {
            console.debug('Action Click', key, item);
          },
        },
      };
    },

    getTemplate() {
      return `
        <base-common-content
          :search="search"
          :placeholder="placeholder"
          :use-search="useSearch"
          :size="size"
          :is-loading="isLoading"
          :is-disabled="isDisabled"
          @search-change="onSearchChange"
        >
          <template #default>
            <base-virtual-scroll-v2
              :items="searchResults"
              :data-key="dataKey"
            >
              <template #default="{ data }">
                <base-item
                  :icon="getItemIcon(data)"
                  :title="getItemTitle(data)"
                  :description="getItemDescription(data)"
                  :status="getItemStatus(data)"
                  :statusType="getItemStatusType(data)"
                  :actions="getItemActions(data)"
                  :actions-type="actionsType"
                  :size="size"
                  :is-disabled="isDisabled"
                  @action-click="onActionClick($event, data)"
                />
              </template>
            </base-virtual-scroll-v2>
          </template>
        </base-common-content>
      `;
    },
  },
});
</script>
